import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useLazyQuery, useMutation } from '@apollo/client';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { formatCurrency } from 'utils';
import { Userpilot } from 'userpilot';
import { useUserPilot } from 'hooks/useUserPilot';
import { VOApi } from 'modules/vendorOpportunity';
import { CalculatedRevenueResponse, CalculatedRevenueVariables } from 'modules/vendorOpportunity/api/types';

export const CalculatedRevenueModal = () => {
  const { isOpen, data, handleClose } = useModal(ModalsKeys.CalculatedRevenueDialog);

  const [fetchCalculatedRevenue, { data: revenueData, loading, error, called }] = useLazyQuery<
    CalculatedRevenueResponse,
    CalculatedRevenueVariables
  >(VOApi.getCalculatedRevenue, { fetchPolicy: 'network-only' });

  const { handleTrackEvent } = useUserPilot();

  const [upsertRevenueFromCalculations, { loading: isSaving }] = useMutation(
    VOApi.upsertVendorOpportunityRevenueFromCalculations,
    { refetchQueries: ['VendorOpportunityRevenueBreakdown'] }
  );

  const handleEntered = () => {
    if (data?.vendorOpportunityId) {
      fetchCalculatedRevenue({ variables: { input: { vendorOpportunityId: data.vendorOpportunityId } } });
    }
  };

  const revenue = revenueData?.vendorOpportunityCalculatedRevenue;

  return (
    <Dialog open={isOpen} fullWidth maxWidth="md" onClose={handleClose} TransitionProps={{ onEntered: handleEntered }}>
      <DialogTitle>
        <Typography component="span" variant="h6">
          Auto-populated Revenue Data
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography color="error">Failed to load calculated revenue data.</Typography>
          </Box>
        )}

        {!loading && !error && called && revenue && (
          <Stack spacing={2}>
            <Section title="Deal Overview">
              <Row label="Opportunity" value={revenue.opportunityName} />
              <Row label="Dealer" value={revenue.dealer} />
              <Row label="Location / Branch" value={revenue.locationBranch} />
              <Row label="Sales Rep" value={revenue.salesRep} />
              <Row label="Deal Date" value={revenue.dealDate} />
              <Row label="Deal Amount" value={formatCurrency(revenue.dealAmount)} />
            </Section>

            <Divider />

            <Section title="Equipment">
              <Row label="Sale Price" value={formatCurrency(revenue.equipmentSalePrice)} />
              <Row label="Cost" value={formatCurrency(revenue.equipmentCost)} />
              <Row label="Gross" value={formatCurrency(revenue.equipmentGross)} />
              <Row label="Margin %" value={formatPercent(revenue.equipmentMarginPercent)} />
            </Section>

            <Divider />

            <Section title="Finance">
              <Row label="Finance Used" value={revenue.financeUsed} />
              <Row label="Lender" value={revenue.lender} />
              <Row label="Finance Gross" value={formatCurrency(revenue.financeGross)} />
              <Row label="Finance Profit" value={formatCurrency(revenue.financeProfit)} />
              <Row label="Margin %" value={formatPercent(revenue.financeMarginPercent)} />
              <Row label="Internal Finance Tier" value={revenue.internalFinanceTier} />
              <Row label="Doc Fee" value={formatCurrency(revenue.docFeeAmount)} />
            </Section>

            <Divider />

            <Section title="ESC">
              <Row label="Sold" value={revenue.escSold} />
              <Row label="Vendor" value={revenue.escVendor} />
              <Row label="Gross" value={formatCurrency(revenue.escGross)} />
              <Row label="Cost" value={formatCurrency(revenue.escCost)} />
              <Row label="Profit" value={formatCurrency(revenue.escProfit)} />
            </Section>

            <Divider />

            <Section title="GAP">
              <Row label="Sold" value={revenue.gapSold} />
              <Row label="Vendor" value={revenue.gapVendor} />
              <Row label="Gross" value={formatCurrency(revenue.gapGross)} />
              <Row label="Cost" value={formatCurrency(revenue.gapCost)} />
              <Row label="Profit" value={formatCurrency(revenue.gapProfit)} />
            </Section>

            <Divider />

            <Section title="Insurance">
              <Row label="Sold" value={revenue.insuranceSold} />
              <Row label="Vendor" value={revenue.insuranceVendor} />
              <Row label="Gross" value={formatCurrency(revenue.insuranceGross)} />
              <Row label="Cost" value={formatCurrency(revenue.insuranceCost)} />
              <Row label="Profit" value={formatCurrency(revenue.insuranceProfit)} />
            </Section>

            <Divider />

            <Section title="Roadside">
              <Row label="Sold" value={revenue.roadsideSold} />
              <Row label="Vendor" value={revenue.roadsideVendor} />
              <Row label="Gross" value={formatCurrency(revenue.roadsideGross)} />
              <Row label="Cost" value={formatCurrency(revenue.roadsideCost)} />
              <Row label="Profit" value={formatCurrency(revenue.roadsideProfit)} />
            </Section>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button size="small" variant="outlined" onClick={handleClose}>
          Cancel
        </Button>

        <Button
          size="small"
          color="error"
          variant="outlined"
          onClick={() => {
            handleTrackEvent({
              event: 'Clicked NOT ACCURATE on auto-populated revenue data',
              metadata: {
                vendorOpportunityId: data?.vendorOpportunityId ?? null,
                page: '/vo',
                lender: revenue?.lender ?? null,
                dealer: revenue?.dealer ?? null,
              },
            });
            Userpilot.trigger('survey:oj8ECQZUoQ');
            handleClose();
          }}
        >
          Not Accurate
        </Button>

        <Button
          size="small"
          color="primary"
          variant="contained"
          disabled={!revenue || isSaving}
          onClick={async () => {
            try {
              await upsertRevenueFromCalculations({
                variables: { input: { vendorOpportunityId: data?.vendorOpportunityId } },
              });
              handleTrackEvent({
                event: 'Clicked VALIDATE on auto-populated revenue data',
                metadata: {
                  vendorOpportunityId: data?.vendorOpportunityId ?? null,
                  page: '/vo',
                  lender: revenue?.lender ?? null,
                  dealer: revenue?.dealer ?? null,
                },
              });
            } catch (e) {
              // mutation error handled by Apollo
            }
            handleClose();
          }}
        >
          {isSaving ? <CircularProgress size={20} /> : 'Validate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Box
        display="grid"
        gridTemplateColumns="1fr 1fr"
        gap={0.5}
        sx={{ '& > *:nth-of-type(odd)': { color: 'text.secondary' } }}
      >
        {children}
      </Box>
    </Box>
  );
}

function Row({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body2">{value || '—'}</Typography>
    </>
  );
}

function formatPercent(value: number | null | undefined): string {
  if (value == null) return '';
  return `${value.toFixed(2)}%`;
}
